import { Typography, useTheme, Box, Modal } from '@mui/material';
import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import { getAllUserWiseContribution } from 'service';
import { userWiseContributionConstants } from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { useSelector, useDispatch } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { getAllPensionPlans } from 'service/OrderService';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';

export interface UserContribution {
  userId: number;
  userName: string;
  ARF: number;
  Annuity: number;
  Cash: number;
  Combo: number;
  total: number;
}

export interface PlansNameType {
  planName: string;
}

export interface PensionPlansType {
  name: string;
}

const UserWiseContribution = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const dispatch = useDispatch();
  const [contributionData, setContributionData] = useState<UserContribution[]>(
    [],
  );
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [plans, setPlans] = useState<PensionPlansType[]>([]);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const pageInfo = useSelector(
    (state) => state?.pagination?.userWiseContribution,
  );
  const getAllContribution = async (searchValue: string): Promise<void> => {
    try {
      const data = await getAllUserWiseContribution(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
      );
      setContributionData(data?.userContribution);
      setPageCount(data?.count);
    } catch (error) {
      console.log(error);
    }
  };

  const ConfirmationDialog = (): JSX.Element => {
    return (
      <Modal
        open={openConfirmationDialog}
        onClose={() => {
          setOpenConfirmationDialog(false);
        }}
      >
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',

            bgcolor: 'background.paper',
            boxShadow: 24,
            py: 5,
            px: 5,
            borderRadius: 5,
          }}
        ></Box>
      </Modal>
    );
  };

  useEffect(() => {
    getAllPensionPlans()
      .then((res: { data: PensionPlansType[] }) => {
        setPlans(res?.data ?? []);
      })
      .catch((err: unknown) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    void getAllContribution(searchValue);
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const plansColumns = plans?.map((plan) => ({
    field: plan?.name,
    flex: 1,
    sortable: false,
    align: 'right',
    renderHeader: () => (
      <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
        {plan?.name}
      </Typography>
    ),
    renderCell: ({ row }) => (
      <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
        {CurrencyFormater(Number(row[plan?.name])) || '-'}
      </Typography>
    ),
  }));

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: userWiseContributionConstants.userIdField,
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userWiseContributionUserIdColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userId}
        </Typography>
      ),
    },
    {
      field: userWiseContributionConstants.userNameField,
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userWiseContributionUserNameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.userName}
        </Typography>
      ),
    },
    ...plansColumns,
    {
      field: userWiseContributionConstants.totalField,
      flex: 1,
      align: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userWiseContributionTotalColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(row?.total) || '-'}
        </Typography>
      ),
    },
  ];

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'userWiseContribution', value: 0 }));
  };

  return (
    <>
      <CustomDataGrid
        columns={columns}
        rows={contributionData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.userId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="userWiseContribution"
        pageCount={pageCount}
        searchEnabled={true}
        searchPlaceholder={strings.displayText.searchLabel2}
        handleSearch={handleSearch}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode: unknown) => {
          dispatch(
            setSortBy({
              key: 'userWiseContribution',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'userWiseContribution',
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: 'userWiseContribution',
              value: 0,
            }),
          );
        }}
      />
      <ConfirmationDialog />
    </>
  );
};

export default UserWiseContribution;
